import React, { useContext, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  Switch,
  Alert,
} from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { GlobalStateContext } from '../../components/general/GlobalStateProvider';
import DataOps from '../../data/DataOps';
import { toast } from 'react-toastify';

interface AccountSettingsProps {
  isEditing?: boolean;
}

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  marketingEmails: boolean;
  sessionReminders: boolean;
  newSessionAlerts: boolean;
}

interface PrivacySettings {
  profileVisibility: 'public' | 'private' | 'friends';
  showEmail: boolean;
  showPhone: boolean;
  allowMessages: boolean;
}

const AccountSettingsPage: React.FC<AccountSettingsProps> = ({ isEditing = false }) => {
  const { padding, user, setUser } = useContext(GlobalStateContext);
  const [isNotificationsExpanded, setIsNotificationsExpanded] = useState(false);
  const [isPrivacyExpanded, setIsPrivacyExpanded] = useState(false);
  const [isSecurityExpanded, setIsSecurityExpanded] = useState(false);
  const [isAccountExpanded, setIsAccountExpanded] = useState(false);
  
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    marketingEmails: true,
    sessionReminders: true,
    newSessionAlerts: true,
  });

  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    profileVisibility: 'public',
    showEmail: false,
    showPhone: false,
    allowMessages: true,
  });

  // Load settings from storage on mount
  useEffect(() => {
    if (user?.userId) {
      loadSettingsFromStorage();
    }
  }, [user?.userId]);

  const loadSettingsFromStorage = async () => {
    try {
      // Load user-specific settings if userId is available
      const notificationKey = user?.userId ? `notificationSettings_${user.userId}` : 'notificationSettings';
      const privacyKey = user?.userId ? `privacySettings_${user.userId}` : 'privacySettings';
      
      const savedNotificationSettings = await DataOps.retrieveDataFromStorage(notificationKey);
      const savedPrivacySettings = await DataOps.retrieveDataFromStorage(privacyKey);
      
      if (savedNotificationSettings) {
        setNotificationSettings(savedNotificationSettings);
      }
      if (savedPrivacySettings) {
        setPrivacySettings(savedPrivacySettings);
      }
    } catch (error) {
      console.error('Error loading settings from storage:', error);
    }
  };

  const updateNotificationSetting = async (key: keyof NotificationSettings, value: boolean) => {
    const updatedSettings = { ...notificationSettings, [key]: value };
    setNotificationSettings(updatedSettings);
    
    // Save with user-specific key
    const storageKey = user?.userId ? `notificationSettings_${user.userId}` : 'notificationSettings';
    await DataOps.saveDataToStorage(storageKey, updatedSettings);
    toast.success('Notification settings updated');
  };

  const updatePrivacySetting = async (key: keyof PrivacySettings, value: any) => {
    const updatedSettings = { ...privacySettings, [key]: value };
    setPrivacySettings(updatedSettings);
    
    // Save with user-specific key
    const storageKey = user?.userId ? `privacySettings_${user.userId}` : 'privacySettings';
    await DataOps.saveDataToStorage(storageKey, updatedSettings);
    toast.success('Privacy settings updated');
  };

  const handleChangePassword = () => {
    toast.info('Password change functionality will be implemented soon');
    // TODO: Implement password change modal
  };

  const handleDeleteAccount = () => {
    Alert.alert(
      'Delete Account',
      'Are you sure you want to delete your account? This action cannot be undone.',
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              // TODO: Implement account deletion
              toast.error('Account deletion functionality will be implemented soon');
            } catch (error) {
              console.error('Error deleting account:', error);
              toast.error('Failed to delete account');
            }
          },
        },
      ]
    );
  };

  const renderSettingItem = (
    icon: string,
    label: string,
    description: string,
    value: boolean,
    onToggle: (value: boolean) => void
  ) => (
    <View style={styles.settingItem}>
      <View style={styles.settingTextContainer}>
        <Text style={styles.settingLabel}>{label}</Text>
        <Text style={styles.settingDescription}>{description}</Text>
      </View>
      <Switch
        value={value}
        onValueChange={onToggle}
        trackColor={{ false: '#d1d5db', true: '#34C759' }}
        thumbColor="#fff"
      />
    </View>
  );

  return (
    <ScrollView contentContainerStyle={[styles.container, {paddingRight: padding, paddingLeft: padding + 50}]}>
      <View style={styles.settingsContainer}>
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons name="cog-outline" size={80} color="#222" />
        </View>

        <View style={styles.titleContainer}>
          <Text style={styles.titleText}>Account Settings</Text>
        </View>

        <Text style={styles.description}>
          Manage your account preferences, notifications, privacy, and security settings.
          <Text style={styles.link}> Learn more</Text>
        </Text>

        <View style={styles.settingsListContainer}>
          {/* Notification Settings */}
          <View style={styles.sectionItem}>
            <MaterialCommunityIcons
              name="bell-outline"
              size={24}
              style={styles.sectionIcon}
            />
            <View style={styles.sectionInfo}>
              <Text style={styles.sectionLabel}>Notifications</Text>
              <Text style={styles.sectionValue}>
                Manage your notification preferences
              </Text>
            </View>
            <Pressable
              style={styles.expandButton}
              onPress={() => setIsNotificationsExpanded(!isNotificationsExpanded)}
            >
              <MaterialCommunityIcons 
                name={isNotificationsExpanded ? "chevron-up" : "chevron-down"} 
                size={20} 
                color="#666" 
              />
            </Pressable>
          </View>
          {isNotificationsExpanded && (
            <View style={styles.expandedContent}>
              <View style={styles.settingsGroup}>
                {renderSettingItem(
                  'email-outline',
                  'Email Notifications',
                  'Receive updates via email',
                  notificationSettings.emailNotifications,
                  (value) => updateNotificationSetting('emailNotifications', value)
                )}
                {renderSettingItem(
                  'bell-outline',
                  'Push Notifications',
                  'Receive push notifications on your device',
                  notificationSettings.pushNotifications,
                  (value) => updateNotificationSetting('pushNotifications', value)
                )}
                {renderSettingItem(
                  'message-text-outline',
                  'SMS Notifications',
                  'Receive text message alerts',
                  notificationSettings.smsNotifications,
                  (value) => updateNotificationSetting('smsNotifications', value)
                )}
                {renderSettingItem(
                  'email-newsletter',
                  'Marketing Emails',
                  'Receive promotional content and updates',
                  notificationSettings.marketingEmails,
                  (value) => updateNotificationSetting('marketingEmails', value)
                )}
                {renderSettingItem(
                  'calendar-clock',
                  'Session Reminders',
                  'Get reminders before your sessions',
                  notificationSettings.sessionReminders,
                  (value) => updateNotificationSetting('sessionReminders', value)
                )}
                {renderSettingItem(
                  'calendar-plus',
                  'New Session Alerts',
                  'Get notified when favorite camps publish new sessions',
                  notificationSettings.newSessionAlerts,
                  (value) => updateNotificationSetting('newSessionAlerts', value)
                )}
              </View>
            </View>
          )}

          {/* Privacy Settings */}
          <View style={styles.sectionItem}>
            <MaterialCommunityIcons
              name="shield-account-outline"
              size={24}
              style={styles.sectionIcon}
            />
            <View style={styles.sectionInfo}>
              <Text style={styles.sectionLabel}>Privacy</Text>
              <Text style={styles.sectionValue}>
                Control your privacy and data sharing
              </Text>
            </View>
            <Pressable
              style={styles.expandButton}
              onPress={() => setIsPrivacyExpanded(!isPrivacyExpanded)}
            >
              <MaterialCommunityIcons 
                name={isPrivacyExpanded ? "chevron-up" : "chevron-down"} 
                size={20} 
                color="#666" 
              />
            </Pressable>
          </View>
          {isPrivacyExpanded && (
            <View style={styles.expandedContent}>
              <View style={styles.settingsGroup}>
                {renderSettingItem(
                  'email-outline',
                  'Show Email',
                  'Display your email on your public profile',
                  privacySettings.showEmail,
                  (value) => updatePrivacySetting('showEmail', value)
                )}
                {renderSettingItem(
                  'phone-outline',
                  'Show Phone',
                  'Display your phone number on your public profile',
                  privacySettings.showPhone,
                  (value) => updatePrivacySetting('showPhone', value)
                )}
                {renderSettingItem(
                  'message-outline',
                  'Allow Messages',
                  'Let other users send you messages',
                  privacySettings.allowMessages,
                  (value) => updatePrivacySetting('allowMessages', value)
                )}
                <View style={styles.settingItem}>
                  <View style={styles.settingTextContainer}>
                    <Text style={styles.settingLabel}>Profile Visibility</Text>
                    <Text style={styles.settingDescription}>Who can see your profile</Text>
                  </View>
                  <View style={styles.visibilityButtons}>
                    {['public', 'private', 'friends'].map((visibility) => (
                      <Pressable
                        key={visibility}
                        style={[
                          styles.visibilityButton,
                          privacySettings.profileVisibility === visibility && styles.visibilityButtonActive
                        ]}
                        onPress={() => updatePrivacySetting('profileVisibility', visibility)}
                      >
                        <Text style={[
                          styles.visibilityButtonText,
                          privacySettings.profileVisibility === visibility && styles.visibilityButtonTextActive
                        ]}>
                          {visibility.charAt(0).toUpperCase() + visibility.slice(1)}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Security Settings */}
          <View style={styles.sectionItem}>
            <MaterialCommunityIcons
              name="shield-lock-outline"
              size={24}
              style={styles.sectionIcon}
            />
            <View style={styles.sectionInfo}>
              <Text style={styles.sectionLabel}>Security</Text>
              <Text style={styles.sectionValue}>
                Manage your account security
              </Text>
            </View>
            <Pressable
              style={styles.expandButton}
              onPress={() => setIsSecurityExpanded(!isSecurityExpanded)}
            >
              <MaterialCommunityIcons 
                name={isSecurityExpanded ? "chevron-up" : "chevron-down"} 
                size={20} 
                color="#666" 
              />
            </Pressable>
          </View>
          {isSecurityExpanded && (
            <View style={styles.expandedContent}>
              <View style={styles.settingsGroup}>
                <Pressable style={styles.actionItem} onPress={handleChangePassword}>
                  <MaterialCommunityIcons name="lock-reset" size={20} color="#34C759" />
                  <Text style={styles.actionItemText}>Change Password</Text>
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#666" />
                </Pressable>
                
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="shield-check" size={20} color="#34C759" />
                  <View style={styles.infoItemText}>
                    <Text style={styles.infoLabel}>Two-Factor Authentication</Text>
                    <Text style={styles.infoValue}>Enabled</Text>
                  </View>
                </View>

                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="fingerprint" size={20} color="#34C759" />
                  <View style={styles.infoItemText}>
                    <Text style={styles.infoLabel}>Biometric Login</Text>
                    <Text style={styles.infoValue}>Enabled</Text>
                  </View>
                </View>

                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="history" size={20} color="#34C759" />
                  <View style={styles.infoItemText}>
                    <Text style={styles.infoLabel}>Last Login</Text>
                    <Text style={styles.infoValue}>Today at 10:30 AM</Text>
                  </View>
                </View>
              </View>
            </View>
          )}

          {/* Account Management */}
          <View style={styles.sectionItem}>
            <MaterialCommunityIcons
              name="account-cog-outline"
              size={24}
              style={styles.sectionIcon}
            />
            <View style={styles.sectionInfo}>
              <Text style={styles.sectionLabel}>Account Management</Text>
              <Text style={styles.sectionValue}>
                Manage your account status
              </Text>
            </View>
            <Pressable
              style={styles.expandButton}
              onPress={() => setIsAccountExpanded(!isAccountExpanded)}
            >
              <MaterialCommunityIcons 
                name={isAccountExpanded ? "chevron-up" : "chevron-down"} 
                size={20} 
                color="#666" 
              />
            </Pressable>
          </View>
          {isAccountExpanded && (
            <View style={styles.expandedContent}>
              <View style={styles.settingsGroup}>
                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="account-check" size={20} color="#34C759" />
                  <View style={styles.infoItemText}>
                    <Text style={styles.infoLabel}>Account Status</Text>
                    <Text style={styles.infoValue}>Active</Text>
                  </View>
                </View>

                <View style={styles.infoItem}>
                  <MaterialCommunityIcons name="calendar-month" size={20} color="#34C759" />
                  <View style={styles.infoItemText}>
                    <Text style={styles.infoLabel}>Member Since</Text>
                    <Text style={styles.infoValue}>January 2024</Text>
                  </View>
                </View>

                <Pressable 
                  style={[styles.actionItem, styles.dangerActionItem]} 
                  onPress={handleDeleteAccount}
                >
                  <MaterialCommunityIcons name="delete-outline" size={20} color="#FF3B30" />
                  <Text style={[styles.actionItemText, styles.dangerActionItemText]}>Delete Account</Text>
                  <MaterialCommunityIcons name="chevron-right" size={20} color="#FF3B30" />
                </Pressable>

                <Text style={styles.warningText}>
                  ⚠️ Deleting your account is permanent and cannot be undone. All your data will be lost.
                </Text>
              </View>
            </View>
          )}
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingTop: 20,
    backgroundColor: '#fff',
    flexGrow: 1,
  },
  settingsContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  iconContainer: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#f8f8f8',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
    borderWidth: 2,
    borderColor: '#e0e0e0',
  },
  titleContainer: {
    marginBottom: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },
  titleText: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
  },
  description: {
    fontSize: 14,
    color: '#555',
    textAlign: 'center',
    marginHorizontal: 24,
    marginBottom: 24,
  },
  link: {
    color: '#008489',
  },
  settingsListContainer: {
    width: '100%',
  },
  sectionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
  },
  sectionIcon: {
    marginRight: 16,
    color: '#333',
  },
  sectionInfo: {
    flex: 1,
  },
  sectionLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  sectionValue: {
    fontSize: 14,
    color: '#666',
  },
  expandButton: {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: 4,
  },
  expandedContent: {
    marginTop: 12,
    marginBottom: 12,
  },
  settingsGroup: {
    padding: 16,
    backgroundColor: '#f8f8f8',
    borderRadius: 8,
  },
  settingItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  settingTextContainer: {
    flex: 1,
    marginRight: 16,
  },
  settingLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  settingDescription: {
    fontSize: 12,
    color: '#666',
  },
  visibilityButtons: {
    flexDirection: 'row',
    gap: 8,
  },
  visibilityButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#d1d5db',
    backgroundColor: '#fff',
  },
  visibilityButtonActive: {
    backgroundColor: '#34C759',
    borderColor: '#34C759',
  },
  visibilityButtonText: {
    fontSize: 12,
    color: '#666',
  },
  visibilityButtonTextActive: {
    color: '#fff',
    fontWeight: '600',
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  actionItemText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginLeft: 12,
  },
  dangerActionItem: {
    borderBottomColor: '#FFE5E5',
  },
  dangerActionItemText: {
    color: '#FF3B30',
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  infoItemText: {
    flex: 1,
    marginLeft: 12,
  },
  infoLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#333',
    marginBottom: 2,
  },
  infoValue: {
    fontSize: 12,
    color: '#666',
  },
  warningText: {
    fontSize: 12,
    color: '#FF3B30',
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#FFE5E5',
    lineHeight: 16,
  },
});

export default AccountSettingsPage;

